import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { sendPasswordResetOTP } from "../apiClient";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import AuthShell from "../components/marketing/AuthShell";
import { Button } from "../components/ui";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function VendorLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
  
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();

      //console.log('data', data);
      
      if (data.session) {
        localStorage.setItem('session', JSON.stringify(data.session));
      }
      
      await checkAuthStatus();
      setMessage("Login successful!");
      
      if (data.redirect) {
        if (data.redirect.type === 'organization') {
          navigate(`/organization/${data.redirect.id}`);
        } else if (data.redirect.type === 'vendor') {
          navigate(`/vendor/${data.redirect.id}`);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    const { data, error } = await sendPasswordResetOTP(email.toLowerCase());

    setLoading(false);
    
    if (error) {
      console.error("Reset error:", error);
      setError("Failed to send reset instructions. Please try again.");
    } else if (data?.success) {
      setMessage(data.message || 'Reset code sent to your email address');
      setIsResetMode(false);
    } else {
      setError('Failed to send reset code. Please try again.');
    }
  };

  return (
    <AuthShell
      title={isResetMode ? "Reset your password" : "District & vendor login"}
      subtitle={isResetMode
        ? "We'll email you reset instructions."
        : "Sign in to manage events, tickets, and reporting."}
      side={{
        heading: "One command center for your whole district.",
        sub: "Athletic directors, fine arts directors, and coordinators manage every event in one place.",
        bullets: ["Create events in minutes", "Scan tickets at the gate", "Real-time revenue & attendance"],
      }}
    >
      {error && <p className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 text-center">{error}</p>}
      {message && <p className="mb-4 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 text-center">{message}</p>}

      <form onSubmit={isResetMode ? handlePasswordReset : handleLogin} className="space-y-4">
        <input
          type="email" placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          className="w-full px-4 py-3 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300"
        />

        {!isResetMode && (
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"} name="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} required
              className="w-full px-4 py-3 pr-11 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600">
              {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
            </button>
          </div>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={loading}>
          {loading ? (isResetMode ? "Sending..." : "Logging in...") : isResetMode ? "Send reset instructions" : "Login"}
        </Button>
      </form>

      <div className="mt-5 text-center">
        <button
          onClick={() => { setIsResetMode(!isResetMode); setError(null); setMessage(null); }}
          className="text-sm text-brand-600 hover:text-brand-700 font-medium"
        >
          {isResetMode ? "← Back to login" : "Forgot password?"}
        </button>
      </div>

      <div className="mt-6 pt-5 border-t border-ink-100 text-center text-sm text-ink-500 space-y-1">
        <p>Need a vendor account? <Link to="/vendor-signup" className="text-brand-600 font-medium hover:underline">Sign up</Link></p>
        <p>Buying tickets? <Link to="/login" className="text-brand-600 font-medium hover:underline">Customer login</Link></p>
      </div>
    </AuthShell>
  );
}

export default VendorLogin;
